import { z } from "zod";
import { IsoDateTime, Uuid, pageOf, PageQuery } from "./common";
import { ClickIds, EventConsent, UtmParams } from "./tracking";

const Short = z.string().trim().min(1).max(200);
const Line = z.string().trim().max(200);
const Text = z.string().trim().max(5000);
const Slug = z.string().trim().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
const HttpsUrl = z.string().trim().max(2000).regex(/^https:\/\//);
const HhMm = z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$|^24:00$/);
const LocalDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const LEAD_LANGUAGES = ["ro", "bg", "hu", "en"] as const;
export const LeadLanguage = z.enum(LEAD_LANGUAGES);
export type LeadLanguage = z.infer<typeof LeadLanguage>;
export const LeadI18n = z.object({ ro: z.string().trim().min(1).max(200), bg: z.string().trim().min(1).max(200), hu: z.string().trim().min(1).max(200), en: z.string().trim().min(1).max(200) });

export const LEAD_SOURCES = ["form", "landing", "meta_form", "google_form", "manual", "import", "host", "mcp"] as const;
export const LeadSource = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof LeadSource>;

export const LEAD_CHANNELS = ["email", "sms", "viber", "whatsapp"] as const;
export const LeadChannel = z.enum(LEAD_CHANNELS);
export type LeadChannel = z.infer<typeof LeadChannel>;

export const LeadAttribution = z.strictObject({
  clicks: ClickIds.optional(),
  clickedAt: IsoDateTime.optional(),
  utm: UtmParams.optional(),
  fbc: z.string().trim().max(600).optional(),
  fbp: z.string().trim().max(200).optional(),
  /** The snippet's visitor: the clicks it stored on landing are copied to the lead. */
  visitorId: z.string().trim().min(8).max(100).optional(),
  landingUrl: z.string().trim().max(2000).optional(),
  referrer: z.string().trim().max(2000).optional(),
  angle: Slug.optional(),
  campaignRef: Short.optional(),
  adSetRef: Short.optional(),
  creativeRef: Short.optional(),
  topicRef: Short.optional(),
  messageRef: Short.optional(),
});
export type LeadAttribution = z.infer<typeof LeadAttribution>;

export const LeadConsentGrant = z.strictObject({
  family: z.enum(["email", "phone"]),
  textVersion: z.string().trim().min(1).max(120),
  at: IsoDateTime,
  ip: z.string().trim().max(64).optional(),
  userAgent: z.string().trim().max(512).optional(),
  hostReference: z.string().trim().min(1).max(200).optional(),
  /** The host attests the mailbox: needs a host reference. */
  hostConfirmedMailbox: z.boolean().optional(),
});
export type LeadConsentGrant = z.infer<typeof LeadConsentGrant>;

// The tenant's own fields on a lead. A value is a string, a number or a boolean; a date is YYYY-MM-DD.
export const LEAD_CUSTOM_FIELD_TYPES = ["text", "number", "date", "select", "checkbox"] as const;
export const LeadCustomFieldType = z.enum(LEAD_CUSTOM_FIELD_TYPES);
export type LeadCustomFieldType = z.infer<typeof LeadCustomFieldType>;
/** At most this many, archived ones included: an archived field still holds answers. */
export const LEAD_CUSTOM_FIELDS_MAX = 30;
export const LEAD_CUSTOM_FIELD_TEXT_MAX = 1000;
/** The types a lead list can filter on. */
export const LEAD_FILTERABLE_FIELD_TYPES = ["select", "checkbox", "date"] as const;
export const LeadCustomFieldKey = z.string().trim().regex(/^[a-z][a-z0-9_]{0,39}$/);
export const LeadCustomFieldValue = z.union([z.string().max(LEAD_CUSTOM_FIELD_TEXT_MAX), z.number(), z.boolean()]);
export type LeadCustomFieldValue = z.infer<typeof LeadCustomFieldValue>;
export const LeadCustomFieldValues = z.record(z.string().max(40), LeadCustomFieldValue);
export type LeadCustomFieldValues = z.infer<typeof LeadCustomFieldValues>;
const LeadFieldLabels = z.partialRecord(LeadLanguage, z.string().trim().min(1).max(120));
export const LeadFieldOption = z.strictObject({ value: z.string().trim().min(1).max(100), label: LeadFieldLabels });
export type LeadFieldOption = z.infer<typeof LeadFieldOption>;
export const LeadFieldDefinitionDto = z.object({
  id: Uuid,
  key: z.string(),
  labels: LeadFieldLabels,
  type: LeadCustomFieldType,
  options: z.array(LeadFieldOption),
  required: z.boolean(),
  position: z.number().int(),
  archived: z.boolean(),
  /** How many leads hold an answer. A field with none can be deleted outright. */
  leadCount: z.number().int(),
  /** How many forms ask for it. */
  formCount: z.number().int(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type LeadFieldDefinitionDto = z.infer<typeof LeadFieldDefinitionDto>;
export const LeadFieldDefinitionInput = z.strictObject({
  key: LeadCustomFieldKey,
  labels: LeadFieldLabels,
  type: LeadCustomFieldType,
  options: z.array(LeadFieldOption).max(50).optional(),
  required: z.boolean().optional(),
});
export type LeadFieldDefinitionInput = z.infer<typeof LeadFieldDefinitionInput>;
/** The key and the type stay as made: leads and forms already use them. */
export const LeadFieldDefinitionPatch = z.strictObject({
  labels: LeadFieldLabels.optional(),
  options: z.array(LeadFieldOption).max(50).optional(),
  required: z.boolean().optional(),
  archived: z.boolean().optional(),
});
export type LeadFieldDefinitionPatch = z.infer<typeof LeadFieldDefinitionPatch>;
export const LeadFieldOrderInput = z.strictObject({ keys: z.array(LeadCustomFieldKey).min(1).max(LEAD_CUSTOM_FIELDS_MAX) });
export type LeadFieldOrderInput = z.infer<typeof LeadFieldOrderInput>;

/** Who the person is to the tenant. A tenant that keeps only providers takes nobody without it being proven. */
export const LeadPersonRole = z.enum(["provider", "customer"]);

export const LeadInput = z
  .strictObject({
    email: z.string().trim().min(3).max(320).optional(),
    phone: z.string().trim().min(5).max(40).optional(),
    firstName: Line.optional(),
    lastName: Line.optional(),
    company: Line.optional(),
    city: Line.optional(),
    locale: LeadLanguage.optional(),
    country: z.string().trim().length(2).optional(),
    message: Text.optional(),
    hostRef: Short.optional(),
    source: LeadSource.default("manual"),
    ownerId: Uuid.optional(),
    attribution: LeadAttribution.optional(),
    consents: z.array(LeadConsentGrant).max(2).optional(),
    occurredAt: IsoDateTime.optional(),
    idempotencyKey: Short.optional(),
    /** The tenant's own fields, by key. */
    fields: LeadCustomFieldValues.optional(),
    role: LeadPersonRole.optional(),
  })
  .refine((lead) => !!lead.email || !!lead.phone, { path: ["email"], message: "email or phone is required" });
export type LeadInput = z.infer<typeof LeadInput>;

export const LeadPatch = z
  .strictObject({
    firstName: Line.nullable().optional(),
    lastName: Line.nullable().optional(),
    company: Line.nullable().optional(),
    city: Line.nullable().optional(),
    locale: LeadLanguage.nullable().optional(),
    ownerId: Uuid.nullable().optional(),
    nextAction: Line.nullable().optional(),
    nextActionAt: IsoDateTime.nullable().optional(),
    /** The tenant's own fields, by key. Null clears one; a key left out stays as it is. */
    fields: z.record(z.string().max(40), LeadCustomFieldValue.nullable()).optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: "nothing to change" });
export type LeadPatch = z.infer<typeof LeadPatch>;

export const LeadConsentState = z.enum(["none", "pending", "granted", "withdrawn"]);
export const LeadFamilyState = z.object({ state: LeadConsentState, textVersion: z.string().nullable(), at: IsoDateTime.nullable(), suppressed: z.boolean() });

/** How soon somebody should look at this lead, most pressing first. The inbox sorts on it and says it in words. */
export const LEAD_URGENCIES = ["late", "replied", "waiting", "step_due", "step_soon", "normal"] as const;
export const LeadUrgency = z.enum(LEAD_URGENCIES);
export type LeadUrgency = z.infer<typeof LeadUrgency>;

export const LeadDto = z.object({
  id: Uuid,
  contactId: Uuid,
  status: z.string(),
  mergedIntoId: Uuid.nullable(),
  stageKey: z.string(),
  stageEnteredAt: IsoDateTime,
  lostReason: z.string().nullable(),
  ownerId: Uuid.nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  company: z.string().nullable(),
  city: z.string().nullable(),
  locale: z.string().nullable(),
  message: z.string().nullable(),
  score: z.number().int(),
  source: z.string(),
  formId: Uuid.nullable(),
  angle: z.string().nullable(),
  campaignRef: z.string().nullable(),
  adSetRef: z.string().nullable(),
  creativeRef: z.string().nullable(),
  topicRef: z.string().nullable(),
  messageRef: z.string().nullable(),
  utm: z.record(z.string(), z.unknown()).nullable(),
  clicks: z.record(z.string(), z.unknown()).nullable(),
  landingUrl: z.string().nullable(),
  referrer: z.string().nullable(),
  /** The tenant's own fields, by key. An archived field's answer stays here. */
  fields: LeadCustomFieldValues,
  reply: z.object({ status: z.string(), channel: z.string().nullable(), at: IsoDateTime.nullable(), skip: z.string().nullable(), confirmationAt: IsoDateTime.nullable() }),
  sla: z.object({ minutes: z.number().int().nullable(), dueAt: IsoDateTime.nullable(), alertedAt: IsoDateTime.nullable(), overdue: z.boolean() }),
  firstContactAt: IsoDateTime.nullable(),
  firstContactVia: z.string().nullable(),
  nextAction: z.string().nullable(),
  nextActionAt: IsoDateTime.nullable(),
  lastInboundAt: IsoDateTime.nullable(),
  lastActivityAt: IsoDateTime,
  urgency: LeadUrgency,
  createdAt: IsoDateTime,
});
export type LeadDto = z.infer<typeof LeadDto>;
export const LeadPage = pageOf(LeadDto);

export const LeadEventDto = z.object({ id: Uuid, type: z.string(), data: z.unknown().nullable(), scoreDelta: z.number().int(), actorName: z.string(), at: IsoDateTime });
export type LeadEventDto = z.infer<typeof LeadEventDto>;
export const LeadNoteDto = z.object({ id: Uuid, body: z.string(), authorName: z.string(), createdAt: IsoDateTime });
export type LeadNoteDto = z.infer<typeof LeadNoteDto>;
export const LeadTaskDto = z.object({ id: Uuid, title: z.string(), dueAt: IsoDateTime.nullable(), ownerId: Uuid.nullable(), doneAt: IsoDateTime.nullable(), createdAt: IsoDateTime });
export type LeadTaskDto = z.infer<typeof LeadTaskDto>;
export const LeadTouchDto = z.object({
  id: Uuid,
  source: z.string(),
  formId: Uuid.nullable(),
  angle: z.string().nullable(),
  campaignRef: z.string().nullable(),
  creativeRef: z.string().nullable(),
  messageRef: z.string().nullable(),
  utm: z.record(z.string(), z.unknown()).nullable(),
  clicks: z.record(z.string(), z.unknown()).nullable(),
  landingUrl: z.string().nullable(),
  occurredAt: IsoDateTime,
});
export type LeadTouchDto = z.infer<typeof LeadTouchDto>;

export const LeadCard = z.object({
  lead: LeadDto,
  consents: z.object({ email: LeadFamilyState, phone: LeadFamilyState }),
  touches: z.array(LeadTouchDto),
  events: z.array(LeadEventDto),
  notes: z.array(LeadNoteDto),
  tasks: z.array(LeadTaskDto),
  whatsappWindowUntil: IsoDateTime.nullable(),
});
export type LeadCard = z.infer<typeof LeadCard>;

export const LeadListQuery = PageQuery.extend({
  cursor: Uuid.optional(),
  stage: z.string().trim().max(64).optional(),
  ownerId: Uuid.optional(),
  source: z.string().trim().max(64).optional(),
  q: z.string().trim().min(2).max(200).optional(),
  overdue: z.enum(["true", "false"]).optional(),
  /** `urgency` is the inbox: leads still in play, the most pressing first. A stage named in `stage` is still honoured. */
  order: z.enum(["recent", "urgency"]).optional(),
  /** One of the tenant's own fields of type select, checkbox or date. */
  fieldKey: LeadCustomFieldKey.optional(),
  /** select: the option's value. checkbox: `true` or `false`. */
  fieldValue: z.string().trim().min(1).max(100).optional(),
  /** date: from this day. */
  fieldFrom: LocalDay.optional(),
  /** date: to this day, included. */
  fieldTo: LocalDay.optional(),
});
export type LeadListQuery = z.infer<typeof LeadListQuery>;

export const LeadIntakeResult = z.object({ lead: LeadDto, created: z.boolean(), duplicate: z.boolean(), mergedLeadIds: z.array(Uuid) });
export type LeadIntakeResult = z.infer<typeof LeadIntakeResult>;

export const StageChangeInput = z.strictObject({ stageKey: z.string().trim().min(1).max(64), lostReason: z.string().trim().min(1).max(500).optional() });
export type StageChangeInput = z.infer<typeof StageChangeInput>;
export const LeadNoteInput = z.strictObject({ body: z.string().trim().min(1).max(5000) });
export const LeadTaskInput = z.strictObject({ title: z.string().trim().min(1).max(200), dueAt: IsoDateTime.optional(), ownerId: Uuid.optional() });
export type LeadTaskInput = z.infer<typeof LeadTaskInput>;
export const LeadTaskPatch = z.strictObject({ title: z.string().trim().min(1).max(200).optional(), dueAt: IsoDateTime.nullable().optional(), ownerId: Uuid.nullable().optional(), done: z.boolean().optional() });
export type LeadTaskPatch = z.infer<typeof LeadTaskPatch>;
export const LeadContactInput = z.strictObject({ via: z.enum(["phone", "email", "sms", "whatsapp", "viber", "meeting", "other"]), note: z.string().trim().max(5000).optional() });
export type LeadContactInput = z.infer<typeof LeadContactInput>;
export const LeadMergeInput = z.strictObject({ absorbedId: Uuid });
export const LeadScoreEventInput = z.strictObject({ type: z.string().trim().min(2).max(80), dedupKey: Short.optional() });

export const LeadImportInput = z.strictObject({
  rows: z.array(z.strictObject({ email: z.string().trim().max(320).optional(), phone: z.string().trim().max(40).optional(), firstName: Line.optional(), lastName: Line.optional(), company: Line.optional(), city: Line.optional(), locale: LeadLanguage.optional(), hostRef: Short.optional(), fields: LeadCustomFieldValues.optional(), role: LeadPersonRole.optional() })).min(1).max(1000),
  /** The proof the person agreed, when there is one. An import with no proof brings no consent. */
  consent: z.strictObject({ family: z.enum(["email", "phone"]), textVersion: z.string().trim().min(1).max(120), at: IsoDateTime, hostReference: z.string().trim().min(1).max(200) }).optional(),
});
export type LeadImportInput = z.infer<typeof LeadImportInput>;
export const LeadImportResult = z.object({ created: z.number().int(), updated: z.number().int(), failed: z.array(z.object({ row: z.number().int(), error: z.string() })) });
export type LeadImportResult = z.infer<typeof LeadImportResult>;

export const LeadInboundInput = z.strictObject({ channel: LeadChannel, at: IsoDateTime.optional() });
export type LeadInboundInput = z.infer<typeof LeadInboundInput>;
export const LeadSendInput = z
  .strictObject({ channel: LeadChannel, templateId: Uuid.optional(), subject: z.string().trim().max(200).optional(), text: z.string().trim().max(5000).optional() })
  .refine((send) => !!send.templateId || !!send.text, { path: ["text"], message: "a template or a text is required" });
export type LeadSendInput = z.infer<typeof LeadSendInput>;
export const LeadSendResult = z.object({ status: z.string(), messageId: Uuid.nullable(), reason: z.string().nullable() });
export type LeadSendResult = z.infer<typeof LeadSendResult>;

// Pipeline
export const LEAD_STAGE_KINDS = ["open", "won", "lost"] as const;
export const LeadStageItem = z.strictObject({ key: Slug, name: LeadI18n, kind: z.enum(LEAD_STAGE_KINDS), scoreThreshold: z.number().int().min(1).max(100).nullable().optional() });
export type LeadStageItem = z.infer<typeof LeadStageItem>;
export const PipelineDto = z.object({ stages: z.array(z.object({ key: z.string(), name: LeadI18n, position: z.number().int(), kind: z.enum(LEAD_STAGE_KINDS), scoreThreshold: z.number().int().nullable() })) });
export type PipelineDto = z.infer<typeof PipelineDto>;
export const PipelineInput = z.strictObject({
  stages: z.array(LeadStageItem).min(3).max(20),
  /** Where the leads of a removed stage go, by the removed key. */
  moveTo: z.record(z.string(), z.string()).optional(),
});
export type PipelineInput = z.infer<typeof PipelineInput>;

export const LeadOwnerInput = z.strictObject({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().max(320).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  slaMinutes: z.number().int().min(1).max(10080).nullable().optional(),
  active: z.boolean().optional(),
  userId: Uuid.nullable().optional(),
});
export type LeadOwnerInput = z.infer<typeof LeadOwnerInput>;
export const LeadOwnerPatch = LeadOwnerInput.partial();
export const LeadOwnerDto = z.object({ id: Uuid, name: z.string(), email: z.string().nullable(), phone: z.string().nullable(), slaMinutes: z.number().int().nullable(), active: z.boolean(), userId: Uuid.nullable() });
export type LeadOwnerDto = z.infer<typeof LeadOwnerDto>;
export const LeadOwnerList = z.object({ items: z.array(LeadOwnerDto) });

export const LeadScoringDto = z.object({
  weights: z.array(z.object({ eventType: z.string(), points: z.number().int(), custom: z.boolean() })),
  decay: z.object({ afterDays: z.number().int(), everyDays: z.number().int(), points: z.number().int() }),
});
export type LeadScoringDto = z.infer<typeof LeadScoringDto>;
export const LeadScoringPatch = z.strictObject({
  /** Null puts the pack's weight back. */
  weights: z.record(z.string().trim().min(2).max(80), z.number().int().min(-100).max(100).nullable()).optional(),
  decay: z.strictObject({ afterDays: z.number().int().min(1).max(365), everyDays: z.number().int().min(1).max(90), points: z.number().int().min(0).max(50) }).optional(),
});
export type LeadScoringPatch = z.infer<typeof LeadScoringPatch>;

// Circuit
/** With a sweep every 10 seconds and the fair dispatcher behind it, this is what keeps the first reply inside the minute. */
export const MAX_REPLY_DELAY_SECONDS = 45;
export const LeadSettingsDto = z.object({
  circuitEnabled: z.boolean(),
  autoReplyEnabled: z.boolean(),
  replyDelaySeconds: z.number().int(),
  replyChannels: z.array(LeadChannel),
  replyMaxPerHour: z.number().int(),
  requireTurnstile: z.boolean(),
  turnstileSiteKey: z.string().nullable(),
  /** The widget's secret is never returned: only its last four characters. */
  turnstileSecretLast4: z.string().nullable(),
  /** A site key and its secret are both saved: only then do the forms show the bot check. */
  turnstileReady: z.boolean(),
  alertEmails: z.array(z.string()),
  alertPhones: z.array(z.string()),
  alertSmsEnabled: z.boolean(),
  alertSmsOutsideHours: z.boolean(),
  slaEnabled: z.boolean(),
  slaMinutes: z.number().int(),
  remindersEnabled: z.boolean(),
  digestEnabled: z.boolean(),
  digestTime: z.string(),
  workDays: z.array(z.number().int()),
  workFrom: z.string(),
  workTo: z.string(),
  holidays: z.array(z.string()),
  autoAssign: z.boolean(),
  bookingStageKey: z.string().nullable(),
  replyPausedUntil: IsoDateTime.nullable(),
  replyProblem: z.object({ kind: z.string(), detail: z.string().nullable(), at: IsoDateTime }).nullable(),
});
export type LeadSettingsDto = z.infer<typeof LeadSettingsDto>;
export const LeadSettingsPatch = z
  .strictObject({
    circuitEnabled: z.boolean(),
    autoReplyEnabled: z.boolean(),
    replyDelaySeconds: z.number().int().min(0).max(MAX_REPLY_DELAY_SECONDS),
    replyChannels: z.array(LeadChannel).min(1).max(4),
    replyMaxPerHour: z.number().int().min(0).max(1000),
    requireTurnstile: z.boolean(),
    turnstileSiteKey: z.string().trim().max(200).nullable(),
    /** Checked at Cloudflare before it is kept; null removes it. */
    turnstileSecretKey: z.string().trim().min(10).max(200).nullable(),
    alertEmails: z.array(z.string().trim().email()).max(10),
    alertPhones: z.array(z.string().trim().min(5).max(40)).max(10),
    alertSmsEnabled: z.boolean(),
    alertSmsOutsideHours: z.boolean(),
    slaEnabled: z.boolean(),
    slaMinutes: z.number().int().min(1).max(10080),
    remindersEnabled: z.boolean(),
    digestEnabled: z.boolean(),
    digestTime: HhMm,
    workDays: z.array(z.number().int().min(1).max(7)).min(1).max(7),
    workFrom: HhMm,
    workTo: HhMm,
    holidays: z.array(LocalDay).max(100),
    autoAssign: z.boolean(),
    bookingStageKey: z.string().trim().max(64).nullable(),
  })
  .partial();
export type LeadSettingsPatch = z.infer<typeof LeadSettingsPatch>;

export const LEAD_TEMPLATE_PURPOSES = ["auto_reply", "sequence", "manual"] as const;
export const LeadTemplateMatch = z.strictObject({ forms: z.array(z.string().trim().min(1).max(60)).max(30).optional(), angles: z.array(z.string().trim().min(1).max(60)).max(30).optional(), sources: z.array(z.string().trim().min(1).max(60)).max(30).optional() });
export const LeadTemplateInput = z.strictObject({
  name: z.string().trim().min(1).max(200),
  purpose: z.enum(LEAD_TEMPLATE_PURPOSES),
  channel: LeadChannel,
  language: LeadLanguage,
  subject: z.string().trim().max(200).nullable().optional(),
  body: z.string().trim().min(1).max(5000),
  match: LeadTemplateMatch.optional(),
  status: z.enum(["draft", "active", "retired"]).optional(),
  whatsappTemplate: z.string().trim().min(1).max(200).nullable().optional(),
});
export type LeadTemplateInput = z.infer<typeof LeadTemplateInput>;
export const LeadTemplatePatch = LeadTemplateInput.partial();
export const LeadTemplateDto = z.object({ id: Uuid, name: z.string(), purpose: z.string(), channel: z.string(), language: z.string(), subject: z.string().nullable(), body: z.string(), match: LeadTemplateMatch, status: z.string(), whatsappTemplate: z.string().nullable(), activatedAt: IsoDateTime.nullable() });
export type LeadTemplateDto = z.infer<typeof LeadTemplateDto>;
export const LeadTemplateList = z.object({ items: z.array(LeadTemplateDto) });

const LeadSequenceTiming = { delayMinutes: z.number().int().min(0).max(129600), workingHoursOnly: z.boolean().default(true) };
/** A message step. Steps saved before tasks existed have no `kind`: they are messages. */
export const LeadSequenceMessageStep = z.strictObject({ kind: z.literal("message").optional(), channel: LeadChannel, templateId: Uuid, ...LeadSequenceTiming });
/** A task for the lead's owner, in each language the team works in. */
export const LeadSequenceTaskStep = z.strictObject({ kind: z.literal("task"), title: z.partialRecord(LeadLanguage, z.string().trim().min(1).max(200)), ...LeadSequenceTiming }).refine((step) => Object.keys(step.title).length > 0, { path: ["title"], message: "a title is required" });
export const LeadSequenceStep = z.union([LeadSequenceTaskStep, LeadSequenceMessageStep]);
export type LeadSequenceStep = z.infer<typeof LeadSequenceStep>;
/** What a message says in one language. Saved as a template with `purpose: sequence`, in the same transaction as the sequence. */
export const LeadSequenceMessageText = z.strictObject({ subject: z.string().trim().max(200).optional(), body: z.string().trim().min(1).max(5000) });
/** On the way in, a message step names a template, brings its texts, or both when it edits the texts of the template it names. */
export const LeadSequenceMessageStepInput = z.strictObject({ kind: z.literal("message").optional(), channel: LeadChannel, templateId: Uuid.optional(), messages: z.partialRecord(LeadLanguage, LeadSequenceMessageText).optional(), ...LeadSequenceTiming }).refine((step) => !!step.templateId || Object.keys(step.messages ?? {}).length > 0, { path: ["messages"], message: "a message is required" });
export const LeadSequenceStepInput = z.union([LeadSequenceTaskStep, LeadSequenceMessageStepInput]);
export type LeadSequenceStepInput = z.infer<typeof LeadSequenceStepInput>;
export const LEAD_SEQUENCE_TRIGGERS = ["new_lead", "stage_entered", "lost", "manual"] as const;
const LeadSequenceShape = z.strictObject({ name: z.string().trim().min(1).max(200), trigger: z.enum(LEAD_SEQUENCE_TRIGGERS), stageKey: z.string().trim().max(64).nullable().optional(), angle: Slug.nullable().optional(), steps: z.array(LeadSequenceStepInput).min(1).max(20), status: z.enum(["draft", "active", "paused"]).optional() });
export const LeadSequenceInput = LeadSequenceShape.refine((sequence) => sequence.trigger !== "stage_entered" || !!sequence.stageKey, { path: ["stageKey"], message: "a stage is required" });
export const LeadSequencePatch = LeadSequenceShape.partial();
export type LeadSequencePatch = z.infer<typeof LeadSequencePatch>;
export type LeadSequenceInput = z.infer<typeof LeadSequenceInput>;
export const LeadSequenceDto = z.object({ id: Uuid, name: z.string(), trigger: z.string(), stageKey: z.string().nullable(), angle: z.string().nullable(), steps: z.array(LeadSequenceStep), status: z.string(), activeLeads: z.number().int() });
export type LeadSequenceDto = z.infer<typeof LeadSequenceDto>;
export const LeadSequenceList = z.object({ items: z.array(LeadSequenceDto) });
export const LeadEnrollInput = z.strictObject({ sequenceId: Uuid });
/** One lead's place in one sequence. */
export const LeadEnrollmentDto = z.object({
  id: Uuid,
  sequenceId: Uuid,
  name: z.string(),
  status: z.string(),
  stopReason: z.string().nullable(),
  stepIndex: z.number().int(),
  totalSteps: z.number().int(),
  nextAt: IsoDateTime.nullable(),
  /** Why the current step is waiting. `dry_run`: messaging is off, so it goes once it's on. */
  heldReason: z.string().nullable(),
  nextStep: z.object({ kind: z.enum(["message", "task"]), channel: LeadChannel.nullable() }).nullable(),
  startedAt: IsoDateTime,
});
export type LeadEnrollmentDto = z.infer<typeof LeadEnrollmentDto>;
/** A sequence this lead could start, and what the rules on sending say about it before anything goes out. */
export const LeadSequenceOption = z.object({
  id: Uuid,
  name: z.string(),
  trigger: z.string(),
  totalSteps: z.number().int(),
  /** It ran on this lead before: starting it again begins at the first step. */
  restart: z.boolean(),
  canStart: z.boolean(),
  /** The message steps that can't reach this person today. The sequence stops at the first one. */
  blocked: z.array(z.object({ step: z.number().int(), channel: LeadChannel, reason: z.string() })),
});
export type LeadSequenceOption = z.infer<typeof LeadSequenceOption>;
export const LeadSequencesView = z.object({
  enrollments: z.array(LeadEnrollmentDto),
  options: z.array(LeadSequenceOption),
  /** Why each family of channels can't reach this person today. Null when it can. */
  reach: z.object({ email: z.string().nullable(), phone: z.string().nullable() }),
});
export type LeadSequencesView = z.infer<typeof LeadSequencesView>;

// Forms and landing pages
export const LEAD_FIELD_TYPES = ["text", "email", "tel", "select", "textarea", "checkbox", "number", "date"] as const;
export const LEAD_FIELD_MAPS = ["firstName", "lastName", "email", "phone", "company", "city", "message", "extra", "custom"] as const;
export const LeadFormField = z.strictObject({
  key: z.string().trim().regex(/^[a-z][a-z0-9_]{0,39}$/),
  type: z.enum(LEAD_FIELD_TYPES),
  mapsTo: z.enum(LEAD_FIELD_MAPS),
  /** With `mapsTo: "custom"`: the key of the tenant's own field this question fills. */
  fieldKey: LeadCustomFieldKey.optional(),
  label: z.partialRecord(LeadLanguage, z.string().trim().min(1).max(200)),
  required: z.boolean().default(false),
  options: z.array(z.strictObject({ value: z.string().trim().min(1).max(100), label: z.partialRecord(LeadLanguage, z.string().trim().min(1).max(200)) })).max(50).optional(),
});
export type LeadFormField = z.infer<typeof LeadFormField>;
export const LeadFormCopy = z.strictObject({ title: z.string().trim().max(200).optional(), intro: z.string().trim().max(1000).optional(), submit: z.string().trim().max(60).optional(), thanks: z.string().trim().max(1000).optional() });
/** Longest value kept from one field of a public form. */
export const PUBLIC_FORM_FIELD_MAX = 5000;

/**
 * What a visitor's browser posts to a public form: as JSON, or as a plain form post with the same names. The names are the form's
 * own fields, so they are not listed. Text is kept (cut at the limit), `true` becomes "1", anything else is dropped without an error:
 * a visitor is shown the form again rather than a refusal.
 */
export const PublicFormSubmission = z
  .record(z.string(), z.unknown())
  .transform((raw): Record<string, string> =>
    Object.fromEntries(Object.entries(raw).flatMap(([key, value]) => (typeof value === "string" ? [[key, value.slice(0, PUBLIC_FORM_FIELD_MAX)]] : value === true ? [[key, "1"]] : []))),
  )
  .describe("The form's own fields, by name. Text is kept, true counts as checked, anything else is ignored.");
export type PublicFormSubmission = z.output<typeof PublicFormSubmission>;

export const LeadFormInput = z.strictObject({
  name: z.string().trim().min(1).max(200),
  fields: z.array(LeadFormField).min(1).max(30),
  copy: z.partialRecord(LeadLanguage, LeadFormCopy).optional(),
  languages: z.array(LeadLanguage).min(1).max(4),
  consentFamilies: z.array(z.enum(["email", "phone"])).max(2).default(["email"]),
  /** Per language. Changing a wording makes a new version; what was agreed before stays on record. */
  consentText: z.partialRecord(LeadLanguage, z.string().trim().min(10).max(2000)).optional(),
  requireTurnstile: z.boolean().default(true),
  redirectUrl: HttpsUrl.nullable().optional(),
  allowedOrigins: z.array(z.string().trim().regex(/^https:\/\/[a-z0-9.-]+(:\d+)?$/i)).max(20).optional(),
  status: z.enum(["draft", "published", "archived"]).optional(),
});
export type LeadFormInput = z.infer<typeof LeadFormInput>;
/** The defaults belong to a new form: a patch that names one field must leave the others as they are. */
export const LeadFormPatch = LeadFormInput.extend({ consentFamilies: z.array(z.enum(["email", "phone"])).max(2).optional(), requireTurnstile: z.boolean().optional() }).partial();
export type LeadFormPatch = z.infer<typeof LeadFormPatch>;
export const LeadFormDto = z.object({
  id: Uuid,
  publicKey: z.string(),
  name: z.string(),
  status: z.string(),
  fields: z.array(LeadFormField),
  copy: z.record(z.string(), LeadFormCopy),
  languages: z.array(z.string()),
  consentFamilies: z.array(z.string()),
  consentVersion: z.number().int(),
  consentText: z.record(z.string(), z.string()),
  requireTurnstile: z.boolean(),
  redirectUrl: z.string().nullable(),
  allowedOrigins: z.array(z.string()),
  pageUrl: z.string(),
  embedCode: z.string(),
  plainHtml: z.string(),
});
export type LeadFormDto = z.infer<typeof LeadFormDto>;
export const LeadFormList = z.object({ items: z.array(LeadFormDto) });

export const LandingContent = z.strictObject({ title: z.string().trim().min(1).max(160), subtitle: z.string().trim().max(300).optional(), body: z.string().trim().max(5000).optional(), bullets: z.array(z.string().trim().min(1).max(200)).max(8).optional(), seoTitle: z.string().trim().max(70).optional() });
export type LandingContent = z.infer<typeof LandingContent>;
export const LandingPageInput = z.strictObject({
  name: z.string().trim().min(1).max(200),
  slug: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{0,48}$/),
  formId: Uuid.nullable().optional(),
  content: z.partialRecord(LeadLanguage, LandingContent),
  languages: z.array(LeadLanguage).min(1).max(4),
  heroUrl: HttpsUrl.nullable().optional(),
  logoUrl: HttpsUrl.nullable().optional(),
  heroAssetId: Uuid.nullable().optional(),
  logoAssetId: Uuid.nullable().optional(),
});
export type LandingPageInput = z.infer<typeof LandingPageInput>;
export const LandingPagePatch = LandingPageInput.omit({ slug: true }).partial();
export type LandingPagePatch = z.infer<typeof LandingPagePatch>;
export const LandingPageDto = z.object({ id: Uuid, publicSlug: z.string(), name: z.string(), status: z.string(), formId: Uuid.nullable(), content: z.record(z.string(), LandingContent), languages: z.array(z.string()), heroUrl: z.string().nullable(), logoUrl: z.string().nullable(), heroAssetId: Uuid.nullable(), logoAssetId: Uuid.nullable(), publishedAt: IsoDateTime.nullable(), url: z.string() });
export type LandingPageDto = z.infer<typeof LandingPageDto>;
export const LandingPageList = z.object({ items: z.array(LandingPageDto) });

// Booking
export const LeadCalendarDto = z.object({ provider: z.string(), configured: z.boolean(), account: z.string().nullable(), calendarId: z.string(), status: z.string(), error: z.string().nullable(), durationMinutes: z.number().int(), stepMinutes: z.number().int(), noticeMinutes: z.number().int(), horizonDays: z.number().int() });
export type LeadCalendarDto = z.infer<typeof LeadCalendarDto>;
export const LeadCalendarPatch = z.strictObject({ calendarId: z.string().trim().min(1).max(200), durationMinutes: z.number().int().min(5).max(480), stepMinutes: z.number().int().min(5).max(240), noticeMinutes: z.number().int().min(0).max(10080), horizonDays: z.number().int().min(1).max(90) }).partial();
export type LeadCalendarPatch = z.infer<typeof LeadCalendarPatch>;
export const CalendarConnectUrl = z.object({ url: z.string() });
export const BookingLinkInput = z.strictObject({ channel: LeadChannel.optional() });
export type BookingLinkInput = z.infer<typeof BookingLinkInput>;
/** `sent` answers a request that named a channel: what the rules on sending did with it. */
export const BookingLinkDto = z.object({ url: z.string(), expiresAt: IsoDateTime, sent: LeadSendResult.nullable() });
export type BookingLinkDto = z.infer<typeof BookingLinkDto>;
export const LeadBookingDto = z.object({ id: Uuid, leadId: Uuid, startAt: IsoDateTime, endAt: IsoDateTime, status: z.string() });
export type LeadBookingDto = z.infer<typeof LeadBookingDto>;
export const LeadBookingList = z.object({ items: z.array(LeadBookingDto) });

// Funnel
export const LeadFunnelQuery = z.object({ from: IsoDateTime.optional(), to: IsoDateTime.optional(), groupBy: z.enum(["source", "angle", "campaign", "creative", "form", "owner"]).optional() });
export type LeadFunnelQuery = z.infer<typeof LeadFunnelQuery>;
export const LeadFunnelRow = z.object({
  key: z.string(),
  leads: z.number().int(),
  contacted: z.number().int(),
  lost: z.number().int(),
  won: z.number().int(),
  stages: z.array(z.object({ key: z.string(), reached: z.number().int(), conversionFromPrevious: z.number().nullable(), medianHoursFromPrevious: z.number().nullable() })),
  medianMinutesToContact: z.number().nullable(),
  medianBusinessMinutesToContact: z.number().nullable(),
  pctContactedUnder5: z.number().nullable(),
  pctContactedUnder15: z.number().nullable(),
  pctContactedUnder60: z.number().nullable(),
  medianSecondsToReply: z.number().nullable(),
  pctRepliedUnder60Seconds: z.number().nullable(),
  slaBreaches: z.number().int(),
  lostReasons: z.array(z.object({ reason: z.string(), count: z.number().int() })),
});
export type LeadFunnelRow = z.infer<typeof LeadFunnelRow>;
export const LeadFunnelDto = z.object({ total: LeadFunnelRow, groups: z.array(LeadFunnelRow) });
export type LeadFunnelDto = z.infer<typeof LeadFunnelDto>;

/** A host event, as the leads module reads it. */
export const LeadHostEvent = z.object({ type: z.string(), eventId: z.string(), occurredAt: IsoDateTime.optional(), consent: EventConsent.optional() });
